import type { FC } from 'react';
import {
  Box,
  Button,
  Grid,
  Paper,
  Typography,
  Skeleton } from '@mui/material';
import { makeStyles } from '@mui/styles';
import type { Theme } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme: Theme) => ({
  errorPaper: {
    backgroundColor: theme.palette.background.paper,
    borderLeft: `${theme.spacing(0.5)} solid ${theme.palette.error.main}`,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  errorTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  errorMessage: {
    marginBottom: theme.spacing(2),
  },
  retryButton: {
    marginTop: theme.spacing(1),
  },
  missingPaper: {
    backgroundColor: theme.palette.background.paper,
    borderLeft: `${theme.spacing(0.5)} solid ${theme.palette.info.main}`,
    padding: theme.spacing(3),
    marginBottom: theme.spacing(2),
  },
  missingTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
  },
  missingMessage: {
    marginBottom: theme.spacing(2),
  },
  noDataBox: {
    textAlign: 'center' as const,
    paddingTop: theme.spacing(6),
    paddingBottom: theme.spacing(6),
  },
  noDataIcon: {
    color: theme.palette.grey[400],
    fontSize: '4rem',
    marginBottom: theme.spacing(2),
  },
  skeletonCard: {
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
  },
  skeletonSection: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
  },
  skeletonGrid: {
    marginTop: theme.spacing(1),
  },
}));

export const LoadingSkeleton: FC = () => {
  const classes = useStyles();
  return (
    <Box>
      <Grid container spacing={3}>
        {[0, 1, 2, 3].map((i) => (
          <Grid item xs={12} sm={6} lg={3} key={i}>
            <Paper elevation={1} className={classes.skeletonCard}>
              <Skeleton variant="text" width="60%" height={20} />
              <Skeleton variant="text" width="80%" height={40} />
              <Skeleton variant="text" width="40%" height={16} />
            </Paper>
          </Grid>
        ))}
      </Grid>
      {['Base Metrics', 'Derived Metrics', 'Executive Metrics'].map((section) => (
        <Paper elevation={1} className={classes.skeletonSection} key={section}>
          <Skeleton variant="text" width="30%" height={28} />
          <Grid container spacing={2} className={classes.skeletonGrid}>
            {[0, 1, 2].map((i) => (
              <Grid item xs={12} sm={4} key={i}>
                <Skeleton variant="text" width="70%" height={20} />
                <Skeleton variant="text" width="50%" height={32} />
              </Grid>
            ))}
          </Grid>
        </Paper>
      ))}
    </Box>
  );
};

type ErrorStateProps = {
  onRetry: () => void;
};

export const ErrorState: FC<ErrorStateProps> = ({ onRetry }) => {
  const classes = useStyles();
  const { t } = useTranslation('revenue');
  return (
    <Box>
      <Paper elevation={1} className={classes.errorPaper}>
        <Typography variant="h6" color="error" className={classes.errorTitle} style={{ color: '#000000' }}>
          {t('error.title', 'Unable to Load Revenue Data')}
        </Typography>
        <Typography color="textSecondary" className={classes.errorMessage} style={{ color: '#000000' }}>
          {t('error.message', 'An error occurred while fetching revenue accounting data. Please try again.')}
        </Typography>
      </Paper>
      <Button
        variant="contained"
        color="primary"
        onClick={onRetry}
        className={classes.retryButton}
      >
        {t('error.retry', 'Try Again')}
      </Button>
    </Box>
  );
};

export const MissingOrderState: FC = () => {
  const classes = useStyles();
  const { t } = useTranslation('revenue');
  return (
    <Box>
      <Paper elevation={1} className={classes.missingPaper}>
        <Typography variant="h6" color="textSecondary" className={classes.missingTitle} style={{ color: '#000000' }}>
          {t('missing.title', 'Order Not Found')}
        </Typography>
        <Typography color="textSecondary" className={classes.missingMessage} style={{ color: '#000000' }}>
          {t('missing.message', 'No order accounting data found. Please go back and select a valid order.')}
        </Typography>
      </Paper>
      <Button
        variant="outlined"
        color="inherit"
        onClick={() => { window.history.back(); }}
      >
        {t('missing.back', 'Go Back')}
      </Button>
    </Box>
  );
};

export const NoRevenueDataYetState: FC = () => {
  const classes = useStyles();
  const { t } = useTranslation('revenue');
  return (
    <Box className={classes.noDataBox}>
      <Box mb={2}>
        <FontAwesomeIcon icon={faChartLine} className={classes.noDataIcon} />
      </Box>
      <Typography variant="h6" gutterBottom style={{ color: '#000000' }}>
        {t('noData.title', 'No Revenue Data Yet')}
      </Typography>
      <Typography variant="body1" style={{ color: '#000000' }}>
        {t(
          'noData.message',
          'Revenue accounting entries have not been generated for this order. '
          + 'This typically occurs when the accounting worker has not yet processed '
          + 'this transaction, or the transaction ID (tcId) was not available.'
        )}
      </Typography>
    </Box>
  );
};
